import type { Card, Suit } from './cards';

export type GamePhase = 'lobby' | 'playing' | 'finished';

export interface GameOptions {
  deckSize: 36 | 52;
  maxPlayers: number;          // 2..6
  /** Allow all players to throw in cards, not just adjacent attackers */
  throwInByAll: boolean;
  /** Allow defender to transfer attack to next player by playing same-rank card (Perevodnoy) */
  transferable: boolean;
  /** Whether to deal cards back up to 6 after each turn from the talon */
  refillHands: boolean;
  /** Limit how many cards can be thrown in per round (classic = 6) */
  maxAttackCards: number;
  /** Visual rotation: orient seats around the table for mobile */
  rotateSeats: boolean;
  /** When true, the room is hidden from the public lobby list — joinable only by code/link */
  isPrivate: boolean;
  /** AI difficulty for any bots in the room. */
  botDifficulty: 'easy' | 'medium' | 'hard';
}

export type BotDifficulty = GameOptions['botDifficulty'];

export const DEFAULT_OPTIONS: GameOptions = {
  deckSize: 36,
  maxPlayers: 4,
  throwInByAll: true,
  transferable: false,
  refillHands: true,
  maxAttackCards: 6,
  rotateSeats: true,
  isPrivate: false,
  botDifficulty: 'medium',
};

export interface PublicPlayer {
  id: string;          // user id
  name: string;
  avatar?: string | null;
  seat: number;
  handCount: number;
  connected: boolean;
  isHost: boolean;
  finished: boolean;   // exited successfully (not durak)
  isBot?: boolean;
}

/** Player view: includes their own cards but only counts for others. */
export interface PlayerView {
  gameId: string;
  code: string;
  phase: GamePhase;
  options: GameOptions;
  trump: Card | null;
  trumpSuit: Suit | null;
  deckCount: number;
  discardCount: number;
  players: PublicPlayer[];
  attackerSeat: number | null;
  defenderSeat: number | null;
  /** Cards on the table: attack with optional defense */
  table: TableSlot[];
  /** True when the defender has committed to take the table cards. The
   *  client uses this to play a "cards fly to defender" animation when
   *  the round resolves. */
  takingDefender: boolean;
  /** Current viewer's hand */
  hand: Card[];
  /** Whose turn it is to act (attack/defend/throw-in) */
  toActSeats: number[];
  /** seat of this viewer */
  yourSeat: number;
  /** When game finishes, the durak's seat (loser); null on draw */
  durakSeat: number | null;
  /** Order in which players exited successfully */
  finishOrder: number[];
  /** Server timestamp for syncing */
  updatedAt: number;
  /** Absolute wall-clock deadline (ms since epoch) for the players currently
   *  to-act. After this passes the server force-resolves with a pass/take
   *  for each remaining seat. The client renders a countdown from this. */
  moveDeadline: number | null;
  /** User ids who have tapped Ready on the end-of-round screen. The next
   *  round auto-starts once every connected human is in this list. */
  readyUserIds: string[];
  /** Message log: brief actions for UX */
  log: GameLogEntry[];
}

export interface TableSlot {
  attack: Card;
  defense: Card | null;
}

export interface GameLogEntry {
  ts: number;
  text: string;
}

export type Move =
  | { kind: 'attack'; card: Card }
  | { kind: 'defend'; slotIndex: number; card: Card }
  | { kind: 'throwIn'; card: Card }
  | { kind: 'transfer'; card: Card }
  | { kind: 'take' }
  | { kind: 'endTurn' };

export interface GameSummary {
  id: string;
  code: string;       // share code
  phase: GamePhase;
  hostId: string;
  hostName: string;
  playerCount: number;
  maxPlayers: number;
  createdAt: number;
}
